import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { WebsiteWizard } from '../website/Wizard';
import type { WebsiteDraft, WebsiteWizardStep } from '../website/types';
import { TemplatePicker, templatePickerIsValid } from '../website/steps/TemplatePicker';
import { Branding, brandingIsValid } from '../website/steps/Branding';
import { Content, contentIsValid } from '../website/steps/Content';
import { PreviewPublish, previewPublishIsValid } from '../website/steps/PreviewPublish';
import { ApiError } from '../api/client';
import {
  createWebsite,
  listWebsites,
  updateWebsite,
  publishWebsite,
  publicURL as websitePublicURL,
  type Website,
  type WebsiteModule,
} from '../api/websites';
import { getOrgGeneral, updateOrganizationGeneral } from '../api/orgs';
import { listTeams, updateTeam, type Team } from '../api/teams';
import { config } from '../config';
import { supportedLanguages } from '../i18n';
import { PageLoader } from '../components/PageLoader';
import { WebsitePreviewDrawer } from '../components/WebsitePreviewDrawer';

// WebsiteEditor is the wizard route at /orgs/:slug/website/:websiteId.
//
// Two entry shapes:
//   - :websiteId = "new"     → on mount, create a new draft with sensible defaults
//                              and replace the URL with the new id (so refresh
//                              doesn't double-create).
//   - :websiteId = <uuid>    → load that website and edit it. 404 routes back
//                              to the list page.
//
// Save discipline:
//   - persistDraft() PATCHes /general (logo + colors), per-team
//     public_visibility, and PUTs the website row. Branding and team
//     toggles are side-effecting on the org as a whole — the URL of
//     /c/<slug> is decided by `publish`, not by save. (Brand colors
//     and team selection are org-level; multiple website variants share
//     them. The wizard makes that trade-off explicit by living on a
//     per-org page.)
//   - onSubmit (Publish button) calls publish on this specific id.

const DEFAULT_MODULES: WebsiteModule[] = [
  'news',
  'schedule',
  'teams',
  'sponsors',
  'about',
  'contact',
  'events',
  'cta_membership',
];

const steps: readonly WebsiteWizardStep[] = [
  {
    id: 'template',
    titleKey: 'website.wizard.steps.template.title',
    descriptionKey: 'website.wizard.steps.template.description',
    Component: TemplatePicker,
    isValid: templatePickerIsValid,
  },
  {
    id: 'branding',
    titleKey: 'website.wizard.steps.branding.title',
    descriptionKey: 'website.wizard.steps.branding.description',
    Component: Branding,
    isValid: brandingIsValid,
  },
  {
    id: 'content',
    titleKey: 'website.wizard.steps.content.title',
    descriptionKey: 'website.wizard.steps.content.description',
    Component: Content,
    isValid: contentIsValid,
  },
  {
    id: 'preview',
    titleKey: 'website.wizard.steps.preview.title',
    descriptionKey: 'website.wizard.steps.preview.description',
    Component: PreviewPublish,
    isValid: previewPublishIsValid,
  },
];

function emptyDraft(): WebsiteDraft {
  return {
    websiteId: '',
    name: '',
    slug: '',
    template: 'classic',
    heroSeed: 0,
    supportedLanguages: ['sv'],
    primaryLanguage: 'sv',
    logoObjectKey: '',
    primaryColor: '',
    accentColor: '',
    mutedColor: '',
    enabledModules: new Set<WebsiteModule>(DEFAULT_MODULES),
    teamSelection: new Map<string, boolean>(),
    teamsInitial: new Map<string, boolean>(),
    teams: [],
    isUpdate: false,
  };
}

// defaultSiteLanguage picks the initial primary language for a new site:
// the admin's current UI language when it's a platform language, else
// Swedish. The backend re-derives a default too, but pre-selecting the UI
// language matches what the admin almost always wants.
function defaultSiteLanguage(uiLang: string): string {
  const code = (uiLang || '').slice(0, 2).toLowerCase();
  return (supportedLanguages as readonly string[]).includes(code) ? code : 'sv';
}

export function WebsiteEditorPage() {
  const { t, i18n } = useTranslation('common');
  const auth = useAuth();
  const navigate = useNavigate();
  const { slug = '', websiteId = '' } = useParams<{ slug: string; websiteId: string }>();
  const token = auth.user?.access_token ?? null;

  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<WebsiteDraft>(emptyDraft);
  const [submitErrorCode, setSubmitErrorCode] = useState<string | null>(null);
  // bootstrapErr surfaces a hard failure during initial load — e.g. the
  // websiteId in the URL was deleted, or a service is unreachable.
  const [bootstrapErr, setBootstrapErr] = useState<string | null>(null);

  // creatingRef guards against parallel POSTs while the first persistDraft
  // call from a fresh /new is still in flight. Without it a fast double-
  // click on Continue / Save would create two rows.
  const creatingRef = useRef(false);

  // skipNextBootstrapRef suppresses the bootstrap re-fire that would
  // otherwise follow our own navigate('replace') from /new → /:id after
  // the first save. Without this, the URL change flips websiteId, which
  // is a dep of `bootstrap`, which re-runs and sets loading=true — that
  // unmounts the WebsiteWizard and resets stepIndex to 0, dumping the
  // user back to step 1 right when they hit the final step. We already
  // have the freshly persisted state in memory; reloading from the
  // server is wasteful AND visually catastrophic.
  const skipNextBootstrapRef = useRef(false);

  // persistedSite is the last server-confirmed Website row, used to
  // compute the live preview URL the drawer shows. Null until the first
  // save (no row exists on the server yet when websiteId === 'new').
  const [persistedSite, setPersistedSite] = useState<Website | null>(null);

  // Preview drawer state. Closed by default in the wizard — wizards are
  // narrower-feeling than the design-mode editor, so we wait for an
  // explicit toggle before reserving half the viewport.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(720);
  // previewTick bumps every time we want to force the iframe to reload
  // (after a save, when the preview URL itself is stable). Encoded as a
  // query param the public renderer ignores.
  const [previewTick, setPreviewTick] = useState(0);
  const drawerIframeRef = useRef<HTMLIFrameElement | null>(null);

  // The wizard is creation-only as of the multi-site rewrite. When
  // websiteId points at an existing row (UUID, not 'new'), redirect
  // into the design page — that's where ongoing edits live now. The
  // wizard itself never loads an existing row.
  useEffect(() => {
    if (websiteId && websiteId !== 'new') {
      navigate(`/orgs/${slug}/website/${websiteId}/design`, { replace: true });
    }
  }, [websiteId, slug, navigate]);

  // Bootstrap: load org-level branding + teams. The draft itself lives
  // entirely in React state until the user clicks Publish on the final
  // step — that's when the first POST creates the row. Users who open
  // the wizard then navigate away leave no orphan "Draft" row behind.
  const bootstrap = useCallback(async () => {
    setLoading(true);
    setBootstrapErr(null);
    try {
      const [general, teamsRes, existingSites] = await Promise.all([
        getOrgGeneral(token, slug).catch(() => null),
        listTeams(token, slug).catch(() => ({ teams: [] as Team[] })),
        listWebsites(token, slug).catch(() => [] as Website[]),
      ]);

      const teams = teamsRes.teams;
      const initial = new Map<string, boolean>();
      const selection = new Map<string, boolean>();
      for (const team of teams) {
        initial.set(team.id, team.public_visibility);
        selection.set(team.id, team.public_visibility);
      }
      const brandingFields = {
        logoObjectKey: general?.logo_object_key ?? '',
        primaryColor: general?.primary_color ?? '',
        accentColor: general?.accent_color ?? '',
        mutedColor: general?.muted_color ?? '',
      };

      // Default slug seed: lowercase the org slug, suffixed with -2/-3/...
      // when the user already has a website on that slug (second-site
      // creation flow). The TemplatePicker step re-syncs from
      // `slugify(name)` while the user is still typing, but if they
      // save the page name unchanged (or hit Publish on step 1 with
      // the default name) we still want a non-clashing slug — otherwise
      // the create POST bounces with 409 on every save.
      const base = (slug || '').toLowerCase();
      const taken = new Set(existingSites.map((s) => s.slug));
      let defaultSlug = base;
      if (taken.has(defaultSlug)) {
        for (let n = 2; n < 100; n++) {
          const candidate = `${base}-${n}`;
          if (!taken.has(candidate)) {
            defaultSlug = candidate;
            break;
          }
        }
      }
      const lang = defaultSiteLanguage(i18n.language);
      setDraft({
        websiteId: '',
        name: '',
        slug: defaultSlug,
        template: 'classic',
        heroSeed: 0,
        supportedLanguages: [lang],
        primaryLanguage: lang,
        ...brandingFields,
        enabledModules: new Set<WebsiteModule>(DEFAULT_MODULES),
        teamSelection: selection,
        teamsInitial: initial,
        teams,
        isUpdate: false,
      });
      setPersistedSite(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // Most likely the user followed a stale link to a deleted draft.
        // Bounce them back to the list.
        navigate(`/orgs/${slug}/website`, { replace: true });
        return;
      }
      setBootstrapErr(err instanceof ApiError ? err.code : 'unknown');
    } finally {
      setLoading(false);
    }
    // i18n.language is intentionally omitted: it only seeds a new draft's
    // default language once at bootstrap. Including it would recreate this
    // callback on any UI-language change and re-fire the bootstrap effect,
    // clobbering an in-progress draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, slug, token]);

  useEffect(() => {
    if (skipNextBootstrapRef.current) {
      skipNextBootstrapRef.current = false;
      return;
    }
    if (websiteId !== 'new') return; // redirect effect will fire
    void bootstrap();
  }, [bootstrap, websiteId]);

  const updateDraft = useCallback((patch: Partial<WebsiteDraft>) => {
    setDraft((d) => ({ ...d, ...patch }));
  }, []);

  const toggleModule = useCallback((key: WebsiteModule) => {
    setDraft((d) => {
      const next = new Set(d.enabledModules);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return { ...d, enabledModules: next };
    });
  }, []);

  const toggleTeam = useCallback((teamID: string, on: boolean) => {
    setDraft((d) => {
      const next = new Map(d.teamSelection);
      next.set(teamID, on);
      return { ...d, teamSelection: next };
    });
  }, []);

  // persistDraft writes branding + teams + website-row state without
  // publishing. Lazy-creates the row the first time it's called from
  // a /new wizard run, then switches to PUT for subsequent saves.
  // Returns the fresh Website (with preview_token) for the
  // PreviewPublish step to iframe. Side effect: updates persistedSite +
  // bumps previewTick so the drawer's iframe reloads to reflect the
  // just-saved state.
  const persistDraft = useCallback(
    async (final: WebsiteDraft): Promise<Website | null> => {
      try {
        await updateOrganizationGeneral(token, slug, {
          logo_object_key: final.logoObjectKey,
          primary_color: final.primaryColor,
          accent_color: final.accentColor,
          muted_color: final.mutedColor,
        });
        await Promise.all(
          final.teams.map(async (team) => {
            const wanted = final.teamSelection.get(team.id) ?? false;
            const before = final.teamsInitial.get(team.id) ?? false;
            if (wanted === before) return;
            await updateTeam(token, slug, team.id, {
              name: team.name,
              description: team.description,
              color: team.color,
              public_visibility: wanted,
            });
          }),
        );
        const payload = {
          name: final.name,
          slug: final.slug,
          template: final.template,
          hero_seed: final.heroSeed,
          enabled_modules: Array.from(final.enabledModules),
          supported_languages: final.supportedLanguages,
          primary_language: final.primaryLanguage,
        };
        let saved: Website;
        if (final.websiteId === '') {
          // First-save path for a /new wizard run — create the row
          // server-side and stamp the new id back into the local draft.
          // creatingRef guards against parallel double-clicks on the
          // step's Save/Continue button.
          if (creatingRef.current) return null;
          creatingRef.current = true;
          try {
            saved = await createWebsite(token, slug, payload);
          } finally {
            creatingRef.current = false;
          }
          setDraft((d) => ({ ...d, websiteId: saved.id }));
          skipNextBootstrapRef.current = true;
          // After creation the user lands on the design page — the
          // wizard itself is creation-only now. Reuse the same path the
          // dashboard's Edit action goes to.
          navigate(`/orgs/${slug}/website/${saved.id}/design`, { replace: true });
        } else {
          saved = await updateWebsite(token, slug, final.websiteId, payload);
        }
        setPersistedSite(saved);
        setPreviewTick((n) => n + 1);
        return saved;
      } catch (err) {
        if (err instanceof ApiError) {
          setSubmitErrorCode(err.code);
        } else {
          setSubmitErrorCode('unknown');
        }
        return null;
      }
    },
    [navigate, slug, token],
  );

  const saveDraft = useCallback(() => persistDraft(draft), [draft, persistDraft]);

  // previewURL is the public iframe URL with the latest preview_token
  // and a cache-busting tick. Empty when the server hasn't issued a
  // token yet (or the website isn't persisted), which surfaces the
  // drawer's "preview unavailable" placeholder.
  const previewURL = useMemo(() => {
    if (!persistedSite?.preview_token) return '';
    // The preview path is keyed on the WEBSITE's slug, not the org
    // slug — each website carries its own slug under /c/<slug>.
    const base = websitePublicURL(config.apiURL, config.publicSitesBaseDomain, persistedSite.slug);
    return `${base}?preview=${encodeURIComponent(persistedSite.preview_token)}&_t=${previewTick}`;
  }, [persistedSite, previewTick]);

  const onSubmit = useCallback(
    async (final: WebsiteDraft) => {
      setSubmitErrorCode(null);
      const saved = await persistDraft(final);
      if (!saved) return; // error already surfaced via setSubmitErrorCode
      try {
        // Use saved.id rather than final.websiteId — for a /new run the
        // closure-captured final still carries the empty id; persistDraft
        // is the source of truth for the newly-minted row.
        await publishWebsite(token, slug, saved.id);
        navigate(`/orgs/${slug}/website`);
      } catch (err) {
        if (err instanceof ApiError) {
          setSubmitErrorCode(err.code);
        } else {
          setSubmitErrorCode('unknown');
        }
      }
    },
    [navigate, persistDraft, slug, token],
  );

  if (loading) {
    return <PageLoader />;
  }
  if (bootstrapErr) {
    return (
      <main className="page page--website">
        <p role="alert" className="error">
          {t(`website.wizard.error.${bootstrapErr}`, {
            defaultValue: t('website.wizard.error.unknown'),
          })}
        </p>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => navigate(`/orgs/${slug}/website`)}
        >
          {t('common.back')}
        </button>
      </main>
    );
  }

  return (
    <main
      className="page page--website"
      style={{ paddingRight: drawerOpen ? `${drawerWidth}px` : undefined }}
    >
      <header className="page__header">
        <h1>
          {websiteId === 'new'
            ? t('website.wizard.createTitle')
            : t('website.wizard.editTitle')}
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setDrawerOpen((o) => !o)}
            aria-expanded={drawerOpen}
          >
            {drawerOpen
              ? t('website.design.previewHide')
              : t('website.design.previewShow')}
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => navigate(`/orgs/${slug}/website`)}
          >
            {t('common.cancel')}
          </button>
        </div>
      </header>
      <WebsiteWizard
        steps={steps}
        draft={draft}
        update={updateDraft}
        toggleModule={toggleModule}
        toggleTeam={toggleTeam}
        saveDraft={saveDraft}
        onSubmit={onSubmit}
        submitErrorCode={submitErrorCode}
      />

      <WebsitePreviewDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={drawerWidth}
        onWidthChange={setDrawerWidth}
        previewURL={previewURL}
        iframeRef={drawerIframeRef}
      />
    </main>
  );
}
